import React from 'react';
import { withRouter, NextRouter } from 'next/router';
import { connect } from 'react-redux';
import { Element } from 'react-scroll';
import { get as _get } from 'lodash';
import { trackNavigatedToPage } from '@surfline/web-common';
import { getAllUserPermissions, getUserIsEntitled } from 'selectors/user';
import { setScrollPosition } from '../../actions/animations';
import { fetchTravelLocationView } from '../../actions/travelLocationView';
import { fetchTopSpots } from '../../actions/topSpots';

import { getTopSpotsScrollPosition } from '../../selectors/animations';
import { getTravelLocationView, getUnits } from '../../selectors/travelLocationView';
import getTopSpots from '../../selectors/topSpots';

import MotionScrollWrapper from '../../components/MotionScrollWrapper';
import Hero from '../../components/Hero';
import TravelSummary from '../../components/TravelSummary';
import ChildGrid from '../../components/ChildGrid';
import RelatedArticles from '../../components/RelatedArticles';
import TopSpots from '../../components/TopSpots';
import TravelMap from '../../components/TravelMap';
import ViewForecastTravel from '../../components/ViewForecastTravel';
import MetaTags from '../../components/MetaTags';

import { ScrollPosition } from '../../types/scrollPosition';
import { TravelLocationView } from '../../types/locationView';
import { Units } from '../../types/units';
import { SpotsTravel } from '../../types/spot';

import { travelPaths } from '../../utils/urls/travelPath';
import { AppDispatch, AppState } from '../../stores';

type Props = {
  locationView: TravelLocationView;
  units: Units;
  doSetTopSpotsPosition: (...args: any) => void;
  topSpots: SpotsTravel;
  topSpotsScrollPosition: ScrollPosition;
  isEntitled: boolean;
  permissions: { [key: string]: boolean };
  doFetchLocationView: (...args: any) => void;
  doFetchTopSpots: (...args: any) => void;
  router?: any;
} & NextRouter;

type State = {};

class TravelLocation extends React.Component<Props, State> {
  componentDidMount() {
    const {
      router: {
        query: { geonameId },
      },
    } = this.props;
    trackNavigatedToPage(`${_get(this.props, 'locationView.taxonomy.name', null)} Beaches`, {
      category: 'beaches',
      geonameId,
      geonameName: _get(this.props, 'locationView.taxonomy.name', null),
    });
  }

  componentDidUpdate(prevProps: Props) {
    /* fetch location view on client route change */
    const {
      doFetchLocationView,
      doFetchTopSpots,
      router: {
        query: { geonameId },
      },
    } = this.props;

    if (
      _get(prevProps, 'locationView.taxonomy.name', null) !==
      _get(this.props, 'locationView.taxonomy.name', null)
    ) {
      trackNavigatedToPage(`${_get(this.props, 'locationView.taxonomy.name', null)} Beaches`, {
        category: 'beaches',
        geonameId,
        geonameName: _get(this.props, 'locationView.taxonomy.name', null),
      });
    }

    if (geonameId !== prevProps.router.query.geonameId) {
      doFetchLocationView(geonameId);
      doFetchTopSpots(geonameId);
    }
  }

  render() {
    const {
      locationView,
      topSpots,
      units,
      doSetTopSpotsPosition,
      topSpotsScrollPosition,
      isEntitled,
      permissions,
    } = this.props;
    const { taxonomy, url, children, travelContent, breadCrumbs } = locationView;
    const name = _get(locationView, 'taxonomy.name', '');
    const title = `Best Beaches in ${name} - Expert Guide to Traveling & Surfing in ${name} - Surfline`;
    const description = `Know Before You Go. Get the best ${name} travel and weather info along with live HD ${name} cams. Access to the world's best surf forecast team at Surfline.`;
    const urlPath = `${travelPaths.base}${url}`;
    const { hasPremiumCamPermissions } = permissions;

    /* Temp fix for travel jpgs routing here */
    if (!travelContent && !breadCrumbs) return null;

    return (
      <div>
        <MetaTags title={title} description={description} urlPath={urlPath} />
        <Hero
          travelContent={travelContent}
          taxonomy={taxonomy}
          breadCrumbs={breadCrumbs}
          imageResizing
        />
        <Element name="sl-travel-anchor" className="sl-travel-anchor" />
        {travelContent && travelContent.media.large ? (
          <TravelSummary travelContent={travelContent} />
        ) : null}
        {!isEntitled && taxonomy ? <ViewForecastTravel taxonomy={taxonomy} /> : null}
        {taxonomy && children && children.length ? (
          <ChildGrid taxonomy={taxonomy} geoChildren={children} imageResizing />
        ) : null}
        {taxonomy && (
          <MotionScrollWrapper scrollPosition={topSpotsScrollPosition.left}>
            <TopSpots
              taxonomy={taxonomy}
              topSpots={topSpots}
              units={units}
              doSetTopSpotsPosition={(pos) => doSetTopSpotsPosition({ top: undefined, left: pos })}
              hasPremiumCamPermissions={hasPremiumCamPermissions}
            />
          </MotionScrollWrapper>
        )}
        {taxonomy && <TravelMap taxonomy={taxonomy} />}
        {travelContent && travelContent.relatedPosts.length ? (
          <RelatedArticles
            relatedArticles={travelContent.relatedPosts.filter((article) => !!article)}
            imageResizing
          />
        ) : null}
      </div>
    );
  }
}

export default connect(
  (state: AppState) => ({
    locationView: getTravelLocationView(state),
    topSpots: getTopSpots(state),
    units: getUnits(state),
    topSpotsScrollPosition: getTopSpotsScrollPosition(state),
    isEntitled: getUserIsEntitled(state),
    permissions: getAllUserPermissions,
  }),
  (dispatch: AppDispatch) => ({
    doSetTopSpotsPosition: (scrollPosition: ScrollPosition) =>
      dispatch(setScrollPosition('topSpots', scrollPosition)),
    doFetchLocationView: (geonameId: string) => dispatch(fetchTravelLocationView(geonameId)),
    doFetchTopSpots: (geonameId: string) => dispatch(fetchTopSpots(geonameId)),
  }),
  // @ts-ignore
)(withRouter(TravelLocation));
